
/** @jsxImportSource @emotion/react */
import { useMemo } from "react";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { useTenantDefectDefinitions } from "../../../Forms/useManagedOptionsHook";
import CategoryConfigForm from "./CategoryConfigForm";
import ColorCodeTriggerForm from "./ColorCodeTriggerForm";
import { getConfiguredCheckMetaFieldNames, getConfiguredFruitFieldNames, getConfiguredLayerFieldNames } from "./CopyMetaConfigForm";
import { getMetricMetaKeysOptions } from "./FieldConfigForm";
import FilterableConfigList from "./FilterableConfigList";
import FlagConfigForm from "./FlagConfigForm";
import { flagOptions } from "./flagConstants";

export const flagMethods = {
    flag_fruit__fruit_field: "flag_fruit__fruit_field",
    flag_check__fruit_field: "flag_check__fruit_field",
    flag_check__individual_defects: "flag_check__individual_defects",
    flag_check__defects: "flag_check__defects",
    flag_check__check_field: "flag_check__check_field",
    flag_check__fruit_flags: "flag_check__fruit_flags",
    flag_layer__layer_field: "flag_layer__layer_field",
    flag_layer__date_diff: "flag_layer__date_diff",
    flag_parent_layers__layer_flags: "flag_parent_layers__layer_flags",
    flag_cycle__check_flags: "flag_cycle__check_flags",
    flag_cycle__fruit_field: "flag_cycle__fruit_field",
    check__smart_sampling: "check__smart_sampling", // * method that calculates synthetic data, upper/lower bound and finds a dominant class
    check__dominant_class: "check__dominant_class", // * method that finds a dominant class for a check
    check__predict_future_pressure: "check__predict_future_pressure", // * method that predicts future pressure for a check
};

const calculationOptions = {
    dominant_class_by_threshold: "dominant_class_by_threshold",
    dominant_class_by_unique_class_count: "dominant_class_by_unique_class_count",
    underweight: "underweight",
    average: "average",
    min: "min",
    max: "max",
    sum: "sum",
    evaluate_formula: "evaluate_formula",
    median: "median",
    count_unique_classes: "count_unique_classes",
    count_below_threshold: "count_below_threshold",
    count_above_threshold: "count_above_threshold",
};

const findDominantClassCalculationSelectOptions = [
    {
        value: "dominant_class_by_threshold",
        label: "Find dominant class by threshold"
    },
    {
        value: "dominant_class_by_unique_class_count",
        label: "Find dominant class by unique class count"
    },
];

const checkFieldCalculationSelectOptions = [
    {
        value: "actual_value",
        label: "Actual value"
    },
    {
        value: "evaluate_formula",
        label: "Evaluate formula"
    },
];
const calculationSelectOptions = [
    {
        value: "underweight",
        label: "Underweight fruit based on reference weight"
    },
    {
        value: "average",
        label: "Average"
    },
    {
        value: "min",
        label: "Minimum value"
    },
    {
        value: "max",
        label: "Maximum value"
    },
    {
        value: "sum",
        label: "Sum all values"
    },
    {
        value: "median",
        label: "Median of all values"
    },
    {
        value: "count_unique_classes",
        label: "Count unique classes"
    },
    {
        value: "count_below_threshold",
        label: "Count below threshold"
    },
    {
        value: "count_above_threshold",
        label: "Count above threshold"
    },
    ...checkFieldCalculationSelectOptions,
    ...findDominantClassCalculationSelectOptions
];

const defaultField = {
    field: {
        text: "Flag...",
        flag_method: null,
    },
    display_filter: {
        fruit_types: [],
        exclude_fruit_types: [],
        layer_types: [],
        // exclude_layer_types: [],
    }
};

const flagMethodShortDescription = (flagMethod) => {
    if (!flagMethod) {
        return "";
    }
    const items = flagMethod.split("_");

    if (items.length === 1) {
        return items[0];
    }

    return `${items[0]} ${items[1]}`;
};

export default function ColorCodeMetricForm({ name, title, fields, description, setArray }) {
    const checkNames = getConfiguredCheckMetaFieldNames();
    const fruitNames = getConfiguredFruitFieldNames();
    const layerNames = getConfiguredLayerFieldNames(true);

    const config = useConfig();
    const metricMetaKeyOptions = getMetricMetaKeysOptions(config);
    const checkFormFieldOptions = checkNames.map((i) => ({ label: i, value: i }));
    const fruitFormFieldOptions = fruitNames.map((i) => ({ label: i, value: i }));
    const layerFormFieldOptions = layerNames.map((i) => ({ label: i, value: i }));

    const fruitTypeOptions = useMemo(() => config?.fruit_types?.map((i) => ({ label: i.text, value: i.value })) || [], [config?.fruit_types]);
    const fruitType = useMemo(() => fields.find((field) => field.flag_method === flagMethods.flag_check__individual_defects)?.fruit_type, [fields]);

    const allDefects = useTenantDefectDefinitions(fruitType);
    const allDefectsOptions = useMemo(() => allDefects.map((i) => ({ label: i.label, value: i.defect_id })), [allDefects]);

    const getFieldTitle = (object) => (
        <span>
            <b>{flagMethodShortDescription(object.flag_method)}:</b> {object.text} {object.flag_name && <small>[flag: {object.flag_name}]</small>} {object.metric_meta_key && <small>[metric: {object.metric_meta_key}]</small>}
        </span>
    );

    const getFieldForm = (object, setObject) => {
        const description_entity_meta = [flagMethods.flag_cycle__check_flags, flagMethods.flag_cycle__fruit_field].includes(object.flag_method) ? "ripening_cycle_meta" : "layer_meta";
        const description_timeserie_meta = [flagMethods.flag_cycle__check_flags, flagMethods.flag_cycle__fruit_field].includes(object.flag_method) ? "check_session_meta" : "check_meta";

        // Helper function to check if a field should be shown based on for_methods and not_for_methods
        const shouldShowField = (field) => {
            if (!field) return false;

            // If for_methods is specified, the flag_method must be in the list
            if (field.for_methods && field.for_methods.length > 0) {
                if (!field.for_methods.includes(object.flag_method)) {
                    return false;
                }
            }

            // If not_for_methods is specified, the flag_method must NOT be in the list
            if (field.not_for_methods && field.not_for_methods.length > 0) {
                if (field.not_for_methods.includes(object.flag_method)) {
                    return false;
                }
            }

            return true;
        };

        const fields = [
            {
                type: "element",
                el: <div className="pt-3">
                    <ColorCodeTriggerForm
                        description="When should this rule be applied? Disable to inherit from this scheme"
                        object={object.trigger || {}}
                        setObject={(trigger) => setObject({ ...object, trigger })} />
                </div>
            },
            {
                type: "header",
                label: "Metric",
            },
            {
                label: "Method",
                type: "single-select",
                description: "Select the flag method",
                name: "flag_method",
                options: [
                    {
                        value: flagMethods.flag_fruit__fruit_field,
                        label: "Flag fruit based on fruit field ( e.g. pressure in spec)"
                    },
                    {
                        value: flagMethods.flag_check__fruit_field,
                        label: "Flag check & layer based on aggregated fruit field ( e.g. avg brix)"
                    },
                    {
                        value: flagMethods.flag_check__individual_defects,
                        label: "Flag check & layer based on individual defects"
                    },
                    {
                        value: flagMethods.flag_check__defects,
                        label: "Flag check & layer based on defects"
                    },
                    {
                        value: flagMethods.flag_check__fruit_flags,
                        label: "Flag check & layer based on fruit flags"
                    },
                    {
                        value: flagMethods.flag_check__check_field,
                        label: "Flag check & layer based on check field"
                    },
                    {
                        value: flagMethods.flag_parent_layers__layer_flags,
                        label: "Flag layer (e.g PO) based on children layer flags"
                    },
                    {
                        value: flagMethods.flag_layer__layer_field,
                        label: "Flag layer based on layer field"
                    },
                    {
                        value: flagMethods.flag_layer__date_diff,
                        label: "Flag layer based on date difference"
                    },
                    {
                        value: flagMethods.flag_cycle__check_flags,
                        label: "Flag session & cycle based on check flags",
                    },
                    {
                        value: flagMethods.flag_cycle__fruit_field,
                        label: "Flag session & cycle based on fruit field",
                    },
                    {
                        value: flagMethods.check__smart_sampling,
                        label: "Use smart sampling to calculate upper/lower bound field & find dominant class."
                    },
                    {
                        value: flagMethods.check__dominant_class,
                        label: "Determine dominant class for check & layer"
                    },
                    {
                        value: flagMethods.check__predict_future_pressure,
                        label: "Predict future pressure for layer"
                    },
                ]
            },
            {
                label: "Label",
                type: "text",
                description: "Give a short recognizable label for this metric.",
                name: "text",
            },
            {
                label: "Defect position",
                name: "defect_position",
                type: "single-select",
                return_array: true,
                multi: true,
                options: config.get_defect_positions().options,
                for_methods: [flagMethods.flag_check__defects],
                onchange_callback: ({ field, value }) => {
                    const metric_meta_key = ["defects", ...(value || []), ...(object.defect_severity || [])].join("_");
                    const flag_name = `flag_${metric_meta_key}`;
                    setObject({ ...object, [field]: value, metric_meta_key, flag_name });
                }
            },
            {
                label: "Defect Severity",
                name: "defect_severity",
                type: "single-select",
                return_array: true,
                multi: true,
                options: config.get_defect_severities().options,
                for_methods: [flagMethods.flag_check__defects],
                onchange_callback: ({ field, value }) => {
                    const metric_meta_key = ["defects", ...(object.defect_position || []), ...(value || [])].join("_");
                    const flag_name = `flag_${metric_meta_key}`;
                    setObject({ ...object, [field]: value, metric_meta_key, flag_name });
                }
            },
            {
                label: "Fruit type",
                type: "single-select",
                description: "Select the fruit type",
                name: "fruit_type",
                options: fruitTypeOptions,
                for_methods: [flagMethods.flag_check__individual_defects],
            },
            {
                label: "Individual defects",
                name: "individual_defects",
                description: "Select the fruit type to filter the defects",
                type: "single-select",
                return_array: true,
                multi: true,
                options: allDefectsOptions,
                for_methods: [flagMethods.flag_check__individual_defects],
            },
            {
                label: "Field",
                type: "single-select",
                description: "Which field to read from.",
                name: "field",
                options: [...fruitFormFieldOptions, ...metricMetaKeyOptions],
                for_methods: [
                    flagMethods.flag_check__fruit_field,
                    flagMethods.flag_fruit__fruit_field,
                    flagMethods.flag_cycle__fruit_field,
                    flagMethods.check__smart_sampling,
                    flagMethods.check__dominant_class
                ],
                onchange_callback: ({ field, value }) => {
                    const flag_name = `flag_${value}`;
                    setObject({ ...object, [field]: value, flag_name });
                }
            },
            {
                label: "Field",
                type: "single-select",
                description: "Which field to read from.",
                name: "field",
                options: [...checkFormFieldOptions, ...metricMetaKeyOptions],
                for_methods: [flagMethods.flag_check__check_field],
                onchange_callback: ({ field, value }) => {
                    const flag_name = `flag_${value}`;
                    setObject({ ...object, [field]: value, flag_name });
                }
            },
            {
                label: "Field",
                type: "single-select",
                description: "Which field to read from.",
                name: "field",
                options: [...layerFormFieldOptions, ...metricMetaKeyOptions],
                for_methods: [flagMethods.flag_layer__layer_field],
                onchange_callback: ({ field, value }) => {
                    const flag_name = `flag_${value}`;
                    setObject({ ...object, [field]: value, flag_name });
                }
            },
            {
                label: "Field",
                type: "text",
                description: "Your field not in the dropdown above? Type it here. e.g. pressure, brix, weight etc.",
                name: "field",
                for_methods: [
                    flagMethods.flag_check__fruit_field,
                    flagMethods.flag_fruit__fruit_field,
                    flagMethods.flag_check__check_field,
                    flagMethods.flag_cycle__fruit_field,
                    flagMethods.check__smart_sampling,
                    flagMethods.check__dominant_class
                ],
            },
            {
                label: "Calculation",
                type: "single-select",
                name: "calculation",
                for_methods: [
                    flagMethods.flag_fruit__fruit_field,
                    flagMethods.flag_check__fruit_field,
                    flagMethods.flag_cycle__fruit_field,
                    flagMethods.check__smart_sampling,
                    flagMethods.check__dominant_class,
                    flagMethods.flag_check__check_field
                ],
                options: (() => {
                    if (flagMethods.check__smart_sampling === object.flag_method || flagMethods.check__dominant_class === object.flag_method) {
                        return findDominantClassCalculationSelectOptions;
                    }
                    if (flagMethods.flag_check__check_field === object.flag_method || flagMethods.flag_fruit__fruit_field === object.flag_method) {
                        return checkFieldCalculationSelectOptions;
                    }
                    return calculationSelectOptions;
                })()
            },
            // * Calculation options - threshold depends on calculation, not flag_method
            ([
                calculationOptions.count_below_threshold,
                calculationOptions.count_above_threshold,
                calculationOptions.dominant_class_by_threshold,
                calculationOptions.dominant_class_by_unique_class_count
            ].includes(object.calculation)) && {
                label: "Threshold",
                type: "number",
                name: "threshold",
            },
            {
                label: "From date",
                type: "single-select",
                name: "from_date",
                multi: true,
                options: layerNames.map((i) => ({ label: i, value: i })),
                for_methods: [flagMethods.flag_layer__date_diff],
            },
            {
                label: "To date",
                type: "single-select",
                multi: true,
                name: "to_date",
                options: layerNames.map((i) => ({ label: i, value: i })),
                for_methods: [flagMethods.flag_layer__date_diff],
            },
            {
                label: "Source flag name",
                type: "text",
                // eslint-disable-next-line max-len
                description: <span>Which flag name to count e.g flag_defects, flag_ripening, flag_average_maturity, intake_status, ripening_status etc.</span>,
                name: "source_flag_name",
                for_methods: [
                    flagMethods.flag_check__fruit_flags,
                    flagMethods.flag_parent_layers__layer_flags,
                    flagMethods.flag_cycle__check_flags
                ],
            },
            {
                label: "Count flag colors",
                type: "single-select",
                name: "count_flag_colors",
                description: "Count the number of flags with a specific color.",
                multi: true,
                options: flagOptions,
                return_array: true,
                for_methods: [
                    flagMethods.flag_check__fruit_flags,
                    flagMethods.flag_parent_layers__layer_flags,
                    flagMethods.flag_cycle__check_flags
                ],
            },
            {
                label: "Meta key for absolute value",
                type: "text",
                description: <span>Under which key to store the absolute metric in the meta data. e.g. will store `&#123;check_location&#125;_{object.metric_meta_key}` and `{object.metric_meta_key}` in {description_entity_meta} and `{object.metric_meta_key}` on {description_timeserie_meta}. Leave empty to not store the metric.</span>,
                name: "metric_meta_key",
                for_methods: [
                    flagMethods.check__smart_sampling,
                    flagMethods.flag_check__fruit_field,
                    flagMethods.flag_check__fruit_flags,
                    flagMethods.flag_check__defects,
                    flagMethods.flag_check__check_field,
                    flagMethods.flag_layer__date_diff,
                    flagMethods.flag_cycle__check_flags,
                    flagMethods.flag_cycle__fruit_field,
                    flagMethods.flag_check__individual_defects,
                    flagMethods.flag_fruit__fruit_field
                ],
            },
            {
                label: "Relative values / Absolute values",
                type: "radio",
                name: "relative_absolute",
                description: "Compare against actual/absolute or relative values? When set to relative use a value between 0 and 1 in your flags config and select the proper denominator.",
                options: [
                    {
                        value: "relative",
                        label: "Relative values"
                    },
                    {
                        value: "absolute",
                        label: "Absolute values"
                    }
                ],
                not_for_methods: [flagMethods.check__predict_future_pressure],
            },
            object.relative_absolute === "relative" && {
                label: "Denominator",
                type: "single-select",
                name: "denominator",
                description: <span>The denominator to use for relative calculations. The relative value will be stored under `&#123;check_location&#125;_{object.metric_meta_key}_relative` and `{object.metric_meta_key}_relative` in {description_entity_meta} and  `{object.metric_meta_key}_relative` on {description_timeserie_meta}.</span>,
                options: [
                    {
                        value: "sample_size",
                        label: "Sample size"
                    },
                    {
                        value: "avos_fruit",
                        label: "Avos fruit"
                    },
                    {
                        value: "100",
                        label: "100"
                    },
                    {
                        value: "sample_size_x_number_of_boxes",
                        label: "Sample size x number of boxes"
                    },
                    {
                        value: "layer_children",
                        label: "Layer children"
                    },
                    {
                        value: "layer_children_with_source_flag",
                        label: "Layer children with source flag"
                    },
                    {
                        value: "layer_art_children",
                        label: "Layer ART children"
                    },
                    {
                        value: "reference_weight_for_box_size",
                        label: "Lower bound reference weight for box size"
                    },
                    {
                        value: "reference_maximum_weight_for_box_size",
                        label: "Upper bound reference weight for box size"
                    },
                ]
            },
            object.calculation === calculationOptions.evaluate_formula && {
                label: "Formula",
                type: "text",
                name: "formula",
                description: object.flag_method === flagMethods.flag_fruit__fruit_field
                    ? "Evaluate a safe mathematical formula per fruit. Supports fruit.field_name, check.field_name and layer.field_name placeholders. Allowed operations: basic arithmetic (+, -, *, /, **), parentheses, and functions (min, max). Example: fruit.pressure * 2 + check.sample_size / 10"
                    : "Evaluate a safe mathematical formula with dynamic placeholders. Supports check.field_name and layer.field_name placeholders. Allowed operations: basic arithmetic (+, -, *, /, **), parentheses, and functions (min, max). Example: max(0, 100 - check.underweight_fruit_relative * 100)",
            },
            {
                type: "header",
                label: "Flag",
                not_for_methods: [flagMethods.check__predict_future_pressure],
            },
            {
                label: "Target flag name",
                type: "text",
                // eslint-disable-next-line max-len
                description: <span>Flag name e.g flag_defects, flag_ripening,  flag_average_maturity etc. The flag color will be stored as `&#123;check_location&#125;_{object.flag_name}` and `{object.flag_name}` in {description_entity_meta} and  `{object.flag_name}` on {description_timeserie_meta}. Reusing the same flag name will create an array of flags colors and use the most negative color as final flag. If method is set to &quot;Flag fruit based on fruit field&quot; the flag is only set on fruit_meta.</span>,
                name: "flag_name",
                not_for_methods: [flagMethods.check__predict_future_pressure],
            },
            {
                label: "Default flag",
                type: "single-select",
                name: "default_flag",
                options: [...flagOptions, { value: "no-update", label: "No Update" }],
                not_for_methods: [flagMethods.check__predict_future_pressure],
            },
            {
                type: "element",
                el: <FlagConfigForm
                    fields={object.flags || []}
                    description="Possible flags for this metric. For relative metrics use a value between 0 and 1. The order matters, the first flag that matches the condition will be used."
                    name="flags"
                    title="Flags"
                    setArray={(flags) => setObject({ ...object, flags })} />,
                not_for_methods: [flagMethods.check__predict_future_pressure],
            },
            {
                type: "header",
                label: "Categories",
                not_for_methods: [flagMethods.check__predict_future_pressure],
            },
            {
                label: "Target category name",
                type: "text",
                description: <span>Category name e.g. firmness_class, ripening_class, color_class etc. The category will be stored as `&#123;check_location&#125;_{object.category_name}` and `{object.category_name}` in {description_entity_meta} and  `{object.category_name}` on {description_timeserie_meta}.</span>,
                name: "category_name",
                not_for_methods: [flagMethods.check__predict_future_pressure],
            },
            {
                label: "Default category",
                type: "text",
                name: "default_category",
                not_for_methods: [flagMethods.check__predict_future_pressure],
            },
            {
                label: "Inherit ripening classes as categories from fruit config",
                name: "inherit_ripening_classes_from_fruit_config",
                type: "single-select",
                options: config.get_fruit_types().flatMap((fruitType) => (fruitType.ripening_fields || []).map((ripeningField) => ({ label: `Classes from ${fruitType.text} - ${ripeningField.label} [${ripeningField.fieldname_getter}]`, value: ripeningField.fieldname_getter }))),
                not_for_methods: [flagMethods.check__predict_future_pressure],
            },
            {
                type: "element",
                el: <CategoryConfigForm
                    fields={object.categories || []}
                    description="Configure categories for this metric."
                    name="categories"
                    useDisplayFilter={false}
                    title="Categories"
                    setArray={(categories) => setObject({ ...object, categories })} />,
                not_for_methods: [flagMethods.check__predict_future_pressure],
            },
        ];

        return fields.filter((i) => i && shouldShowField(i)); // filter out undefined and fields that don't match the current flag_method
    };

    const fields_checked_for_error = fields.map((i) => ({ ...i, error: !flagMethods[i.flag_method] }));
    const has_undefined_field = fields_checked_for_error.some((i) => i.error);
    const error = has_undefined_field ? "Some fields are using deprecated methods or are not properly defined." : false;

    return (
        <FilterableConfigList
            name={name}
            title={title}
            description={description}
            fields={fields_checked_for_error}
            error={error}
            setArray={setArray}
            defaultField={defaultField}
            getFieldForm={getFieldForm}
            getFieldTitle={getFieldTitle}
            useDisplayFilter={false}
        />
    );
}
